/**
 * GPX Generator Utility
 * Generates valid GPX XML files from adventure route data
 */

import type { RouteTravelMode } from '../types/workflow.types'

// Route data structure for GPX generation
export interface GpxRouteData {
  id: string
  label: string
  travelMode: RouteTravelMode
  routePoints: [number, number][]  // Array of [lat, lng]
  distanceMeters?: number
  durationSeconds?: number
}

// Waypoint data for location nodes
export interface GpxWaypointData {
  id: string
  name: string
  description?: string
  latitude: number
  longitude: number
  type?: 'start' | 'end' | 'location'
}

// GPX export options
export interface GpxExportOptions {
  name: string                      // Adventure name
  description?: string              // Adventure description
  author?: string                   // Creator name
  routes: GpxRouteData[]            // Routes to include
  waypoints?: GpxWaypointData[]     // Optional waypoints
  combinedFile?: boolean            // Single file or separate per route
}

// Map travel mode to GPX type attribute
function getTravelModeType(mode: RouteTravelMode): string {
  switch (mode) {
    case 'foot-walking':
    case 'foot-hiking':
      return 'Foot'
    case 'cycling':
    case 'cycling-electric':
      return 'Bike'
    case 'driving':
      return 'Car'
    case 'public-transit':
      return 'Transit'
    case 'ferry':
      return 'Boat'
    case 'water-kayak':
      return 'Paddle'
    case 'wheelchair':
      return 'Wheelchair'
    default:
      return 'Other'
  }
}

// Get human-readable travel mode label
function getTravelModeLabel(mode: RouteTravelMode): string {
  switch (mode) {
    case 'foot-walking':
      return 'Walking'
    case 'foot-hiking':
      return 'Hiking'
    case 'cycling':
      return 'Cycling'
    case 'cycling-electric':
      return 'E-Bike'
    case 'driving':
      return 'Driving'
    case 'public-transit':
      return 'Public Transit'
    case 'ferry':
      return 'Ferry'
    case 'water-kayak':
      return 'Kayak/Paddle'
    case 'wheelchair':
      return 'Wheelchair'
    default:
      return mode
  }
}

// Escape XML special characters
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

// Format distance for display
function formatDistance(meters?: number): string {
  if (!meters) return ''
  if (meters < 1000) {
    return `${Math.round(meters)}m`
  }
  return `${(meters / 1000).toFixed(2)}km`
}

// Format duration for display
function formatDuration(seconds?: number): string {
  if (!seconds) return ''
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

// Generate GPX XML header
function generateGpxHeader(name: string, description?: string, author?: string): string {
  const timestamp = new Date().toISOString()

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1"
     creator="Hidden Adventures"
     xmlns="http://www.topografix.com/GPX/1/1"
     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
     xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${escapeXml(name)}</name>${description ? `
    <desc>${escapeXml(description)}</desc>` : ''}${author ? `
    <author>
      <name>${escapeXml(author)}</name>
    </author>` : ''}
    <time>${timestamp}</time>
    <link href="https://hiddenadventures.com">
      <text>Hidden Adventures</text>
    </link>
  </metadata>`
}

// Generate GPX waypoint element
function generateWaypoint(waypoint: GpxWaypointData): string {
  const typeSymbol = waypoint.type === 'start' ? 'Flag, Green'
                   : waypoint.type === 'end' ? 'Flag, Red'
                   : 'Pin, Blue'

  return `
  <wpt lat="${waypoint.latitude}" lon="${waypoint.longitude}">
    <name>${escapeXml(waypoint.name)}</name>${waypoint.description ? `
    <desc>${escapeXml(waypoint.description)}</desc>` : ''}
    <sym>${typeSymbol}</sym>
  </wpt>`
}

// Generate GPX route element (for route planning apps)
function generateRoute(route: GpxRouteData): string {
  const modeType = getTravelModeType(route.travelMode)
  const modeLabel = getTravelModeLabel(route.travelMode)
  const distanceInfo = formatDistance(route.distanceMeters)
  const durationInfo = formatDuration(route.durationSeconds)

  const descParts: string[] = []
  if (modeLabel) descParts.push(`Mode: ${modeLabel}`)
  if (distanceInfo) descParts.push(`Distance: ${distanceInfo}`)
  if (durationInfo) descParts.push(`Duration: ${durationInfo}`)

  const routePoints = route.routePoints.map((point, index) => {
    // point is [lat, lng]
    return `    <rtept lat="${point[0]}" lon="${point[1]}">
      <name>Point ${index + 1}</name>
    </rtept>`
  }).join('\n')

  return `
  <rte>
    <name>${escapeXml(route.label)}</name>
    <desc>${escapeXml(descParts.join(' | '))}</desc>
    <type>${modeType}</type>
${routePoints}
  </rte>`
}

// Generate GPX track element (for recording apps - more detailed)
function generateTrack(route: GpxRouteData): string {
  const modeType = getTravelModeType(route.travelMode)
  const modeLabel = getTravelModeLabel(route.travelMode)
  const distanceInfo = formatDistance(route.distanceMeters)
  const durationInfo = formatDuration(route.durationSeconds)

  const descParts: string[] = []
  if (modeLabel) descParts.push(`Mode: ${modeLabel}`)
  if (distanceInfo) descParts.push(`Distance: ${distanceInfo}`)
  if (durationInfo) descParts.push(`Duration: ${durationInfo}`)

  const trackPoints = route.routePoints.map(point => {
    // point is [lat, lng]
    return `      <trkpt lat="${point[0]}" lon="${point[1]}"></trkpt>`
  }).join('\n')

  return `
  <trk>
    <name>${escapeXml(route.label)}</name>
    <desc>${escapeXml(descParts.join(' | '))}</desc>
    <type>${modeType}</type>
    <trkseg>
${trackPoints}
    </trkseg>
  </trk>`
}

// Generate GPX footer
function generateGpxFooter(): string {
  return `
</gpx>`
}

/**
 * Generate a complete GPX file as a string
 * Uses track format (trk) which is more widely supported for navigation
 */
export function generateGpxString(options: GpxExportOptions): string {
  const parts: string[] = []

  // Header
  parts.push(generateGpxHeader(options.name, options.description, options.author))

  // Waypoints
  if (options.waypoints && options.waypoints.length > 0) {
    for (const waypoint of options.waypoints) {
      parts.push(generateWaypoint(waypoint))
    }
  }

  // Tracks (using track format for better GPS app compatibility)
  for (const route of options.routes) {
    if (route.routePoints.length > 0) {
      parts.push(generateTrack(route))
    }
  }

  // Footer
  parts.push(generateGpxFooter())

  return parts.join('')
}

/**
 * Generate a GPX file as a Blob for download
 */
export function generateGpxBlob(options: GpxExportOptions): Blob {
  const gpxString = generateGpxString(options)
  return new Blob([gpxString], { type: 'application/gpx+xml' })
}

/**
 * Generate and download a GPX file
 */
export function downloadGpx(options: GpxExportOptions, filename?: string): void {
  const blob = generateGpxBlob(options)
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename || `${options.name.replace(/[^a-zA-Z0-9]/g, '_')}.gpx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}

/**
 * Generate multiple separate GPX files (one per route)
 * Returns array of {filename, blob} pairs
 */
export function generateSeparateGpxFiles(
  options: GpxExportOptions
): Array<{ filename: string; blob: Blob }> {
  const files: Array<{ filename: string; blob: Blob }> = []

  for (const route of options.routes) {
    if (route.routePoints.length === 0) continue

    const singleRouteOptions: GpxExportOptions = {
      name: route.label,
      description: `Route from ${options.name}`,
      author: options.author,
      routes: [route],
      waypoints: [], // Don't include waypoints in individual route files
    }

    const blob = generateGpxBlob(singleRouteOptions)
    const filename = `${route.label.replace(/[^a-zA-Z0-9]/g, '_')}.gpx`

    files.push({ filename, blob })
  }

  return files
}

/**
 * Download multiple GPX files as a ZIP archive
 * Note: This requires JSZip library to be installed
 */
export async function downloadGpxArchive(
  options: GpxExportOptions,
  archiveName?: string
): Promise<void> {
  // Dynamic import to avoid bundling JSZip if not used
  const JSZip = (await import('jszip')).default

  const zip = new JSZip()
  const files = generateSeparateGpxFiles(options)

  for (const file of files) {
    zip.file(file.filename, file.blob)
  }

  // Also include combined file if there are multiple routes
  if (files.length > 1) {
    const combinedBlob = generateGpxBlob(options)
    zip.file('all_routes.gpx', combinedBlob)
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' })
  const url = URL.createObjectURL(zipBlob)

  const link = document.createElement('a')
  link.href = url
  link.download = archiveName || `${options.name.replace(/[^a-zA-Z0-9]/g, '_')}_routes.zip`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}
